import {
  DefaultResourceConfig,
  useFetchProjectLaunchQueuesQuery,
  useViewerDestinationProjectsQuery,
  useViews2MetadataQuery,
  useViews2ViewQuery,
} from '../../generated/graphql';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../../pages/EntityLaunchPage';
import {useViewer} from '../../state/viewer/hooks';
import {useQueues} from './Queues/hooks';
import {EntityOptions} from './SelectProject2';
import {CREATE_QUEUE_PLACEHOLDER, QueueOptions} from './SelectQueue';
import {
  CREATE_QUEUE_PLACEHOLDER as CREATE_QUEUE_PLACEHOLDER2,
  GroupedQueueOptions,
  QueueOption as QueueOption2,
} from './SelectQueue2';
import {constructQueueOptions, sortQueues} from './utils';

type AccessorFunc = (a: any) => string;
const sortSpecialFirst = (special: string, accessor: AccessorFunc) => {
  return (a: any, b: any) => {
    const l1 = accessor(a);
    const l2 = accessor(b);
    if (l1 === l2) {
      return 0;
    }
    if (l1 === special) {
      return -1;
    }
    if (l2 === special) {
      return 1;
    }
    return caseInsensitiveCompare(l1, l2);
  };
};

export const caseInsensitiveCompare = (a: string, b: string) => {
  const lowerA = a.toLocaleLowerCase();
  const lowerB = b.toLocaleLowerCase();
  return lowerA.localeCompare(lowerB);
};

// Query for the available launch queues and return a sorted array in the select widget format.
export const useEntityQueueOptions = (
  entityName: string,
  projectName: string,
  defaultResourceConfigs?: Record<string, DefaultResourceConfig>,
  includeCreate: boolean = false
) => {
  const configs = defaultResourceConfigs ?? {};
  const projectRunQueuesQuery = useFetchProjectLaunchQueuesQuery({
    variables: {
      entityName,
      projectName,
    },
  });
  const skipEntityQuery = projectName === DEFAULT_LAUNCH_PROJECT_NAME;
  const entityRunQueuesQuery = useFetchProjectLaunchQueuesQuery({
    variables: {
      entityName,
      projectName: DEFAULT_LAUNCH_PROJECT_NAME,
    },
    skip: skipEntityQuery,
  });
  const projectQueues = constructQueueOptions(projectRunQueuesQuery, configs);
  sortQueues(projectQueues);

  const entityQueues = constructQueueOptions(entityRunQueuesQuery, configs);
  sortQueues(entityQueues);
  let queues: QueueOptions = [];
  if (
    entityQueues.length === 0 ||
    projectName === DEFAULT_LAUNCH_PROJECT_NAME
  ) {
    queues = projectQueues;
    if (queues.length === 0 && includeCreate) {
      queues.push(CREATE_QUEUE_PLACEHOLDER);
    }
  } else if (projectQueues.length === 0) {
    queues = entityQueues;
    if (queues.length === 0 && includeCreate) {
      queues.push(CREATE_QUEUE_PLACEHOLDER);
    }
  } else {
    queues = [
      {
        label: 'Launch',
        options: entityQueues,
      },
      {
        label: projectName,
        options: projectQueues,
      },
    ];
  }

  return {
    queues,
    loading: projectRunQueuesQuery.loading || entityRunQueuesQuery.loading,
    error: projectRunQueuesQuery.error || entityRunQueuesQuery.error,
  };
};

// Alternate "GetQueueOptions" (renamed to EntityQueueOptions) that shows all queues, not just the ones for the project.
export const useViewerQueueOptions = (
  configs: Record<string, DefaultResourceConfig>,
  includeGlobalQueues: boolean
) => {
  const viewer = useViewer();
  const personalEntity = viewer?.username ?? '';

  const allQueuesQuery = useQueues(undefined, false, includeGlobalQueues);
  const queueOptions: GroupedQueueOptions[] = [];

  let group = null;
  let groupOptions: QueueOption2[] = [];
  for (const queue of allQueuesQuery.queues) {
    if (queue.entityName !== group) {
      group = queue.entityName;
      groupOptions = [];
      queueOptions.push({
        label: queue.entityName,
        options: groupOptions,
      });
    }

    const defaultResourceConfigID = queue.defaultResourceConfigID ?? '';
    const drc = configs[defaultResourceConfigID] ?? {};
    const resourceType = drc.resource ?? 'unknown';
    const config = drc?.config?.resource_args[resourceType] ?? {};

    const queueOption: QueueOption2 = {
      isActive: queue.status === 'active',
      value: `${queue.entityName}/${queue.name}`,
      entity: queue.entityName,
      project: 'model-registry',
      queue: queue.name,
      queueID: queue.id,
      defaultResourceConfigID,
      resourceType,
      config,
    };
    groupOptions.push(queueOption);
  }

  if (queueOptions.length === 0) {
    queueOptions.push({
      label: personalEntity,
      options: [CREATE_QUEUE_PLACEHOLDER2],
    });
  }

  return {
    queues: queueOptions,
    loading: allQueuesQuery.loading,
    error: allQueuesQuery.error,
  };
};

// Runs have a default color, but user can set manually.
// This hook extracts the project's view spec and run color information.
export const useProjectView = (entityName: string, projectName: string) => {
  const queryViewMetadata = useViews2MetadataQuery({
    variables: {
      entityName,
      name: projectName,
      viewType: 'project-view',
    },
  });
  const viewEdges = queryViewMetadata.data?.project?.allViews?.edges ?? [];
  let viewId = '';
  if (viewEdges.length > 0) {
    viewId = viewEdges[0].node?.id ?? '';
  }
  const queryView = useViews2ViewQuery({
    variables: {
      id: viewId,
    },
    skip: !viewId,
  });

  const specStr = queryView.data?.view?.spec ?? '{}';
  const spec = JSON.parse(specStr);
  const customRunColors = spec?.section?.customRunColors ?? {};

  return {
    viewId,
    spec,
    customRunColors,
    loading: queryViewMetadata.loading || queryView.loading,
    error: queryViewMetadata.error || queryView.error,
  };
};

// We pass the entity/project we are in so we can sort them first.
export const useDestinationProjectOptions = (
  entityName: string,
  projectName: string
) => {
  const query = useViewerDestinationProjectsQuery();
  const entityEdges = query.data?.viewer?.teams?.edges ?? [];
  const entitySort = sortSpecialFirst(entityName, a => a.label);
  const options: EntityOptions[] = entityEdges
    .map(entityEdge => {
      const entityLabel = entityEdge.node?.name ?? '';
      const projectEdges = entityEdge.node?.projects?.edges ?? [];
      const projectSort =
        entityLabel === entityName
          ? sortSpecialFirst(projectName, a => a.projectName)
          : (a: any, b: any) =>
              caseInsensitiveCompare(a.projectName, b.projectName);
      const projectOptions = projectEdges
        .map(projectEdge => {
          const projectId = projectEdge.node?.id ?? '';
          const projectLabel = projectEdge.node?.name ?? '';
          return {
            value: `${entityLabel}/${projectLabel}`,
            entityName: entityLabel,
            projectId,
            projectName: projectLabel,
          };
        })
        .sort(projectSort);
      return {
        label: entityLabel,
        options: projectOptions,
      };
    })
    .sort(entitySort);
  return {
    options,
    loading: query.loading,
    error: query.error,
  };
};
